/**
 * O que a Home do PDV precisa DIZER ao operador em cada estado.
 *
 * Por que este arquivo existe
 * ---------------------------
 * A Home é a primeira tela do balcão, e ela tinha estados MUDOS: quando falta o
 * endereço da loja, quando ninguém entrou, quando o caixa deste dispositivo
 * ainda não foi escolhido, quando o período não foi aberto ou quando nenhuma
 * opção de venda está ativa, a tela mostra o logo, o título e mais nada. Todos
 * esses caminhos se resolvem CLICANDO NO LOGO — que não parece um botão. Quem
 * está no caixa não tem como adivinhar.
 *
 * Este módulo é só a decisão: dado o estado, qual é a situação e o que dizer.
 * Quem desenha é `pages/Home/components/OrientacaoDaHome.tsx`. Módulo puro, sem
 * React, testável — o mesmo padrão de `dimensaoDaConfig.ts`.
 *
 * O ESTADO "pronto" NÃO FALA NADA — e isso é de propósito
 * ------------------------------------------------------
 * Com a loja ligada, o operador dentro, o caixa escolhido, o período aberto e as
 * opções na tela, a Home já se explica sozinha: os botões grandes com o nome da
 * opção de venda SÃO a instrução. Encher essa tela de texto seria ruído no lugar
 * em que ela funciona.
 *
 * Tem também uma consequência de contrato, e ela não é acidente: as onze telas
 * do gabarito visual, menos as duas de fora do login, são fotografadas no estado
 * pronto. Como o estado pronto não emite nada, a orientação entra exatamente
 * onde a tela estava muda e em lugar nenhum além disso — nenhuma foto de 1440
 * muda por causa dela, fora as que já eram exceção assinada
 * (`_gabarito-visual/CARD-29-excecoes.md`).
 *
 * A ORDEM DAS PERGUNTAS IMPORTA
 * -----------------------------
 * Cada estado só faz sentido depois do anterior estar resolvido: não adianta
 * pedir para abrir o período de um caixa que ainda não foi escolhido, nem pedir
 * login sem a loja conectada. A ordem abaixo é a ordem real da primeira
 * instalação no balcão.
 */

/** O que está faltando, na ordem em que é resolvido no balcão. */
export type ChaveDaSituacao =
  | "sem-loja"
  | "sem-login"
  | "sem-caixa"
  | "sem-opcoes"
  | "sem-periodo"
  | "pronto";

/**
 * `instrucao` é o caminho normal do dia a dia (entrar, abrir o período).
 * `atencao` é configuração que falta e que trava a venda — merece destaque
 * diferente, mas nunca alarme: não há nada quebrado, só falta um passo.
 */
export type TomDaOrientacao = "instrucao" | "atencao";

export type SituacaoDaHome = {
  chave: ChaveDaSituacao;
  tom: TomDaOrientacao;
  /** Uma linha dizendo o que está faltando. Vazio no estado pronto. */
  titulo: string;
  /** O passo seguinte, em palavras de balcão. Vazio no estado pronto. */
  texto: string;
};

export type EstadoDaHome = {
  /** `subsidiaryVirtualAddress` — o endereço da API da loja. */
  temEnderecoDaLoja: boolean;
  /** `authUserZustand` — alguém entrou com usuário e senha. */
  estaLogado: boolean;
  /** `selectedPdv.itemUuid` — este dispositivo já foi amarrado a um caixa. */
  temCaixaEscolhido: boolean;
  /** `selectedPdv.selectedPeriod` — o período (caixa) está aberto. */
  periodoAberto: boolean;
  /** Quantas opções de venda estão ativas (`itemInfoStatus`) neste caixa. */
  opcoesDeVendaAtivas: number;
};

const PRONTO: SituacaoDaHome = {
  chave: "pronto",
  tom: "instrucao",
  titulo: "",
  texto: "",
};

/**
 * A situação da Home agora. Uma função só, para que a tela não volte a espalhar
 * `if` de estado no meio do JSX.
 */
export function lerSituacaoDaHome(estado: EstadoDaHome): SituacaoDaHome {
  if (!estado.temEnderecoDaLoja) {
    return {
      chave: "sem-loja",
      tom: "atencao",
      titulo: "Este dispositivo ainda não está ligado a uma loja",
      texto:
        "Toque no logo do Alfredo, aqui em cima, e informe o endereço da loja. " +
        "É a primeira configuração deste computador — depois dela o PDV já abre pronto.",
    };
  }

  if (!estado.estaLogado) {
    return {
      chave: "sem-login",
      tom: "instrucao",
      titulo: "Toque em Acessar para entrar",
      texto:
        "Use o seu usuário e a sua senha do Alfredo. " +
        "As opções de venda aparecem assim que você entrar.",
    };
  }

  if (!estado.temCaixaEscolhido) {
    return {
      chave: "sem-caixa",
      tom: "atencao",
      titulo: "Falta escolher o caixa deste dispositivo",
      texto:
        "Toque no logo do Alfredo → Parametrizações e diga qual caixa é este computador. " +
        "É essa escolha que traz as opções de venda para a tela.",
    };
  }

  if (estado.opcoesDeVendaAtivas === 0) {
    return {
      chave: "sem-opcoes",
      tom: "atencao",
      titulo: "Nenhuma opção de venda está ativa neste caixa",
      texto:
        "Toque no logo do Alfredo → Parametrizações para ativar Atendimento no Local, " +
        "Pedir e Levar ou Entrega a Domicílio.",
    };
  }

  if (!estado.periodoAberto) {
    return {
      chave: "sem-periodo",
      tom: "instrucao",
      titulo: "O período ainda não foi aberto",
      texto:
        "Toque em uma opção de venda — ou no logo do Alfredo → Período — para abrir o caixa " +
        "e começar a registrar pedidos.",
    };
  }

  return PRONTO;
}
